import thunk from "redux-thunk";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { reducer as formReducer } from "redux-form";

import {
  productCatalogueReducer,
  productDevelopmentReducer,
  supplierNetworkReducer,
  inventoryManagementReducer,
  luxurySellerReducer,
  luxuryCollectionReducer,
  luxuryItemListingsReducer,
} from "./modules";
import { applicationReducer } from "./lib/common";
import {
  activationReducer,
  authenticationReducer,
  passwordReducer,
  registrationReducer,
} from "./lib/authentication";
import { accountReducer, subAccountReducer } from "./lib/account";

import { fileReducer, imageReducer } from "./lib/file";

const enhancers = [];
const middleware = [thunk];

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line no-underscore-dangle
  const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__;

  if (typeof devToolsExtension === "function") {
    enhancers.push(devToolsExtension({ trace: true, traceLimit: 25 }));
  }
}

const composedEnhancers = compose(applyMiddleware(...middleware), ...enhancers);

const persistConfig = {
  key: "root",
  storage,
  blacklist: [
    "product_catalogue",
    "product_development",
    "supplier_network",
    "luxury_item_listings",

    // //////////////////////////////////////////////
    // //////////      LIBRARY STORES      //////////
    // //////////////////////////////////////////////
    // lib.common
    "lib_application",

    // lib.account
    "lib_account",
    "lib_sub_account",

    // lib.authentication
    "lib_activation",
    "lib_authentication",
    "lib_password",
    "lib_registration",

    // lib.collaboration
    "lib_group_management",
    "lib_group_membership",

    // lib.file
    "lib_file",
    "lib_image",

    // lib.comments
    "lib_discussion",
    "lib_guarded_comments",

    //////////////////////////////////////////////
    //////////    THIRD-PARTY STORES    //////////
    //////////////////////////////////////////////
    "form",
  ],
};
//////////////////////////////////////////////
//////////       DOMAIN CONFIGS     //////////
//////////////////////////////////////////////
const productDevelopmentPersistConfig = {
  key: "product_development",
  storage,
  blacklist: [],
};

const productCataloguePersistConfig = {
  key: "product_catalogue",
  storage,
  blacklist: [],
};

const supplierNetworkPersistConfig = {
  key: "supplier_network",
  storage,
  blacklist: [],
};

const luxurySellerPersistConfig = {
  key: "luxury_seller",
  storage,
  blacklist: [],
};

const luxuryCollectionPersistConfig = {
  key: "luxury_collection",
  storage,
  blacklist: [],
};

const luxuryItemListingsReducerPersistConfig = {
  key: "luxury_item_listings",
  storage,
  blacklist: ["contactUsMessageSent", "contactUsName", "orderSuccessRefNumber"],
  whitelist: ["shoppingCart", "cartShortId", "cartHasService"],
};

//////////////////////////////////////////////
//////////      LIBRARY CONFIGS     //////////
//////////////////////////////////////////////

const applicationPersistConfig = {
  key: "lib_application",
  storage,
  blacklist: ["processing", "success", "error"],
};

const authenticationPersistConfig = {
  key: "lib_authentication",
  storage,
  blacklist: [],
};

const registrationPersistConfig = {
  key: "lib_registration",
  storage,
  blacklist: [],
};

const accountPersistConfig = {
  key: "lib_account",
  storage,
  blacklist: [],
};

const subAccountPersistConfig = {
  key: "lib_sub_account",
  storage,
  blacklist: [],
};

const rootReducer = combineReducers({
  product_catalogue: persistReducer(
    productCataloguePersistConfig,
    productCatalogueReducer
  ),
  product_development: persistReducer(
    productDevelopmentPersistConfig,
    productDevelopmentReducer
  ),
  supplier_network: persistReducer(
    supplierNetworkPersistConfig,
    supplierNetworkReducer
  ),
  inventory_management: inventoryManagementReducer,
  luxury_seller: persistReducer(luxurySellerPersistConfig, luxurySellerReducer),
  luxury_collection: persistReducer(
    luxuryCollectionPersistConfig,
    luxuryCollectionReducer
  ),
  luxury_item_listings: persistReducer(
    luxuryItemListingsReducerPersistConfig,
    luxuryItemListingsReducer
  ),

  //////////////////////////////////////////////
  //////////     LIBRARY REDUCERS     //////////
  //////////////////////////////////////////////

  // lib.common
  lib_application: persistReducer(applicationPersistConfig, applicationReducer),

  // lib.authentication
  lib_activation: activationReducer,
  lib_authentication: persistReducer(
    authenticationPersistConfig,
    authenticationReducer
  ),
  lib_password: passwordReducer,
  lib_registration: persistReducer(
    registrationPersistConfig,
    registrationReducer
  ),

  // lib.account
  lib_account: persistReducer(accountPersistConfig, accountReducer),
  lib_sub_account: persistReducer(subAccountPersistConfig, subAccountReducer),

  // lib.file
  lib_file: fileReducer,
  lib_image: imageReducer,

  //////////////////////////////////////////////
  //////////   THIRD-PARTY REDUCERS   //////////
  //////////////////////////////////////////////
  form: formReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const configureStore = (preloadedState) => {
  const store = createStore(
    persistedReducer,
    preloadedState,
    composedEnhancers
  );

  const persistor = persistStore(store);
  return { store, persistor };
};

export default configureStore;
